import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Grid,
  Alert,
  CircularProgress,
  Switch,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Business,
  Satellite,
  Refresh,
  Settings,
  Sync,
} from '@mui/icons-material';
import { locationsService, Location } from '../services/locationsService';

const CAPABILITIES = [
  { key: 'RECEPTION', label: 'Réception' },
  { key: 'PREP_MECANIQUE', label: 'Préparation Mécanique' },
  { key: 'LAB_ICP', label: 'Laboratoire ICP' },
  { key: 'LAB_SAA', label: 'Laboratoire SAA' },
  { key: 'LAB_AU', label: 'Laboratoire AU' },
  { key: 'LAB_VOLUMETRIE', label: 'Laboratoire Volumétrie' },
  { key: 'VALIDATION', label: 'Validation' },
  { key: 'CERTIFICATE', label: 'Certificats' },
  { key: 'TRANSFER_SEND', label: 'Envoi de Transferts' },
  { key: 'TRANSFER_RECEIVE', label: 'Réception de Transferts' },
  { key: 'ADMIN', label: 'Administration' },
];

interface LocationFormData {
  code: string;
  name: string;
  type: 'MAIN_LAB' | 'SATELLITE';
  capabilities: string[];
  address: string;
  city: string;
  vpn_ip: string;
  api_endpoint: string;
  sends_to: string;
  is_active: boolean;
}

const initialFormData: LocationFormData = {
  code: '',
  name: '',
  type: 'SATELLITE',
  capabilities: ['RECEPTION', 'PREP_MECANIQUE', 'TRANSFER_SEND'],
  address: '',
  city: '',
  vpn_ip: '',
  api_endpoint: '',
  sends_to: '',
  is_active: true,
};

const LocationManagement: React.FC = () => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [formData, setFormData] = useState<LocationFormData>(initialFormData);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadLocations();
  }, []);

  const loadLocations = async () => {
    try {
      setLoading(true);
      const data = await locationsService.getAll();
      setLocations(data);
    } catch (err) {
      console.error('Failed to load locations:', err);
      setError('Erreur lors du chargement des sites');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (location?: Location) => {
    if (location) {
      setEditingLocation(location);
      setFormData({
        code: location.code,
        name: location.name,
        type: location.type,
        capabilities: location.capabilities,
        address: location.address || '',
        city: location.city || '',
        vpn_ip: location.vpn_ip || '',
        api_endpoint: location.api_endpoint || '',
        sends_to: location.sends_to || '',
        is_active: location.is_active,
      });
    } else {
      setEditingLocation(null);
      setFormData(initialFormData);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingLocation(null);
    setFormData(initialFormData);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      if (editingLocation) {
        await locationsService.update(editingLocation.id, formData);
        setSuccess('Site mis à jour avec succès');
      } else {
        await locationsService.create(formData as any);
        setSuccess('Site créé avec succès');
      }

      handleCloseDialog();
      loadLocations();
    } catch (err: any) {
      console.error('Failed to save location:', err);
      setError(err.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (location: Location) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le site "${location.name}"?`)) {
      return;
    }

    try {
      await locationsService.delete(location.id);
      setSuccess('Site supprimé avec succès');
      loadLocations();
    } catch (err) {
      console.error('Failed to delete location:', err);
      setError('Erreur lors de la suppression');
    }
  };

  const handleCapabilityChange = (capability: string) => {
    setFormData(prev => ({
      ...prev,
      capabilities: prev.capabilities.includes(capability)
        ? prev.capabilities.filter(c => c !== capability)
        : [...prev.capabilities, capability],
    }));
  };

  const getLocationIcon = (type: string) => {
    return type === 'MAIN_LAB' ? <Business color="primary" /> : <Satellite color="secondary" />;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          <Settings sx={{ mr: 1, verticalAlign: 'middle' }} />
          Gestion des Sites
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadLocations}
          >
            Actualiser
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Nouveau Site
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Site</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Capacités</TableCell>
                  <TableCell>IP VPN</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Sync</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {locations.map((location) => (
                  <TableRow key={location.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getLocationIcon(location.type)}
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {location.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {location.city}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={location.code} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={location.type === 'MAIN_LAB' ? 'Principal' : 'Satellite'}
                        color={location.type === 'MAIN_LAB' ? 'primary' : 'secondary'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {location.capabilities.slice(0, 3).map((cap) => (
                          <Chip
                            key={cap}
                            label={CAPABILITIES.find(c => c.key === cap)?.label || cap}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.65rem', height: 20 }}
                          />
                        ))}
                        {location.capabilities.length > 3 && (
                          <Chip
                            label={`+${location.capabilities.length - 3}`}
                            size="small"
                            sx={{ fontSize: '0.65rem', height: 20 }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {location.vpn_ip || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={location.is_active ? 'Actif' : 'Inactif'}
                        color={location.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={location.last_sync_at ? `Dernière sync: ${new Date(location.last_sync_at).toLocaleString()}` : 'Jamais synchronisé'}>
                        <Chip
                          icon={<Sync />}
                          label={location.sync_status}
                          size="small"
                          color={location.sync_status === 'SYNCED' ? 'success' : 'default'}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Modifier">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(location)}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(location)}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {locations.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="text.secondary">
                        Aucun site configuré
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingLocation ? 'Modifier le Site' : 'Nouveau Site'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Code *"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="BOUMADINE"
                disabled={!!editingLocation}
                helperText="Identifiant unique (ex: BOUMADINE)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nom *"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Laboratoire Boumadine"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Type *</InputLabel>
                <Select
                  value={formData.type}
                  label="Type *"
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                >
                  <MenuItem value="MAIN_LAB">Laboratoire Principal</MenuItem>
                  <MenuItem value="SATELLITE">Site Satellite</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Ville"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Adresse"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="IP VPN"
                value={formData.vpn_ip}
                onChange={(e) => setFormData({ ...formData, vpn_ip: e.target.value })}
                placeholder="10.0.0.2"
                helperText="Adresse IP du tunnel VPN"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="API Endpoint"
                value={formData.api_endpoint}
                onChange={(e) => setFormData({ ...formData, api_endpoint: e.target.value })}
                placeholder="http://10.0.0.2:3000"
              />
            </Grid>
            {formData.type === 'SATELLITE' && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Envoie vers</InputLabel>
                  <Select
                    value={formData.sends_to}
                    label="Envoie vers"
                    onChange={(e) => setFormData({ ...formData, sends_to: e.target.value })}
                  >
                    <MenuItem value="">-- Sélectionner --</MenuItem>
                    {locations
                      .filter(l => l.type === 'MAIN_LAB')
                      .map(l => (
                        <MenuItem key={l.id} value={l.id}>
                          {l.name} ({l.code})
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Capacités
              </Typography>
              <FormGroup row>
                {CAPABILITIES.map((cap) => (
                  <FormControlLabel
                    key={cap.key}
                    control={
                      <Checkbox
                        checked={formData.capabilities.includes(cap.key)}
                        onChange={() => handleCapabilityChange(cap.key)}
                        size="small"
                      />
                    }
                    label={cap.label}
                  />
                ))}
              </FormGroup>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="Site actif"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !formData.code || !formData.name}
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            {editingLocation ? 'Mettre à jour' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LocationManagement;
